import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Upload, Megaphone, Power, SlidersHorizontal } from "lucide-react";
import AdImage from "@/components/AdImage";
import api, { formatApiError } from "@/lib/api";

const EMPTY = { title: "", subtitle: "", image_url: "", link: "", zone: "landing", start_date: "", end_date: "", active: true };
const fieldClass = "w-full rounded-lg bg-surface glow-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-lime-400";
const ZONE_LABEL = { landing: "Landing", ticker: "Ticker", queue: "Queue", showcase: "Showcase" };

function AdForm({ onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_000_000) { toast.error("Image must be under 1MB"); return; }
    const reader = new FileReader();
    reader.onload = () => set({ image_url: reader.result });
    reader.readAsDataURL(file);
  };

  const create = async () => {
    if (!form.title.trim()) { toast.error("Give the ad a title"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ads", {
        ...form,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      });
      toast.success("Ad created");
      onCreated(data);
      setForm(EMPTY);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="ad-create-form">
      <h4 className="font-display font-bold flex items-center gap-2 mb-4"><Plus className="w-4 h-4 text-primary-c" /> New Campaign</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="ad-title" placeholder="Ad title / headline" value={form.title} onChange={(e) => set({ title: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }} />
        <select data-testid="ad-zone" value={form.zone} onChange={(e) => set({ zone: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }}>
          <option value="landing">Landing page slot</option>
          <option value="ticker">Live scoreboard ticker</option>
          <option value="queue">Queue / scoreboard screen</option>
          <option value="showcase">Showcase carousel (⅓ screen)</option>
        </select>
        <input data-testid="ad-subtitle" placeholder="Subtitle / tagline (optional)" value={form.subtitle} onChange={(e) => set({ subtitle: e.target.value })} className={`${fieldClass} sm:col-span-2`} style={{ color: "var(--text)" }} />
        <input data-testid="ad-link" placeholder="Click-through URL (optional)" value={form.link} onChange={(e) => set({ link: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }} />
        <input data-testid="ad-image-url" placeholder="Image URL (optional)" value={form.image_url?.startsWith("data:") ? "" : form.image_url} onChange={(e) => set({ image_url: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }} disabled={form.image_url?.startsWith("data:")} />
        <div className="flex items-center gap-2">
          <label className="text-xs mono" style={{ color: "var(--text-2)" }}>Start</label>
          <input data-testid="ad-start" type="date" value={form.start_date} onChange={(e) => set({ start_date: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }} />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs mono" style={{ color: "var(--text-2)" }}>End</label>
          <input data-testid="ad-end" type="date" value={form.end_date} onChange={(e) => set({ end_date: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-3">
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="ad-upload-btn"
          className="glass rounded-lg px-3 py-2 text-sm flex items-center gap-2 hover:scale-[1.02] transition-transform" style={{ color: "var(--text)" }}>
          <Upload className="w-4 h-4 text-primary-c" /> Upload creative
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} className="hidden" data-testid="ad-file-input" />
        {form.image_url && (
          <div className="flex items-center gap-2">
            <AdImage src={form.image_url} className="h-10 w-20 object-cover rounded-md" testid="ad-form-preview" />
            <button onClick={() => set({ image_url: "" })} className="text-xs" style={{ color: "var(--text-2)" }}>clear</button>
          </div>
        )}
        <button data-testid="ad-create-submit" onClick={create} disabled={busy}
          className="btn-glow rounded-full px-5 py-2 text-sm font-bold flex items-center gap-2 ml-auto disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create Ad
        </button>
      </div>
    </div>
  );
}

function AdCard({ ad, onChange, onDelete }) {
  const toggle = async () => {
    try {
      const { data } = await api.patch(`/admin/ads/${ad.ad_id}`, { active: !ad.active });
      onChange(data);
    } catch (e) { toast.error("Update failed"); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/ads/${ad.ad_id}`); onDelete(ad.ad_id); toast.success("Ad deleted"); }
    catch (e) { toast.error("Delete failed"); }
  };
  return (
    <div data-testid={`ad-card-${ad.ad_id}`} className="rounded-2xl glow-border p-4 flex gap-4" style={{ background: "var(--surface)", opacity: ad.active ? 1 : 0.55 }}>
      <AdImage src={ad.image_url} alt={ad.title} className="h-16 w-28 object-cover rounded-lg shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="font-display font-bold truncate">{ad.title}</span>
          <span className="text-[10px] mono uppercase tracking-wider glass rounded-full px-2 py-0.5 shrink-0" style={{ color: "var(--text-2)" }}>
            {ZONE_LABEL[ad.zone] || "Landing"}
          </span>
        </div>
        {ad.subtitle && <div className="text-xs mt-0.5 truncate" style={{ color: "var(--text-2)" }}>{ad.subtitle}</div>}
        <div className="text-xs mt-1 mono" style={{ color: "var(--text-2)" }}>
          {ad.start_date || "—"} → {ad.end_date || "—"}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button data-testid={`ad-toggle-${ad.ad_id}`} onClick={toggle}
            className="glass rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1.5" style={{ color: ad.active ? "var(--primary)" : "var(--text-2)" }}>
            <Power className="w-3.5 h-3.5" /> {ad.active ? "Active" : "Disabled"}
          </button>
          <button data-testid={`ad-delete-${ad.ad_id}`} onClick={remove}
            className="glass rounded-full px-3 py-1 text-xs font-bold flex items-center gap-1.5 hover:text-red-400 transition-colors" style={{ color: "var(--text-2)" }}>
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function PlacementSettings() {
  const [z, setZ] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/config/ad-settings").then(({ data }) => setZ(data)).catch(() => setZ({ landing: true, ticker: true, queue: true, showcase: true, showcase_size: "large" }));
  }, []);

  if (!z) return null;

  const toggle = async (zone) => {
    const prev = z;
    const next = { ...z, [zone]: !z[zone] };
    setZ(next); setBusy(true);
    try { const { data } = await api.put("/admin/ad-settings", next); setZ(data); toast.success("Placement updated"); }
    catch (e) { toast.error("Update failed"); setZ(prev); }
    finally { setBusy(false); }
  };

  const setSize = async (showcase_size) => {
    const prev = z;
    const next = { ...z, showcase_size };
    setZ(next); setBusy(true);
    try { const { data } = await api.put("/admin/ad-settings", next); setZ(data); toast.success("Showcase size updated"); }
    catch (e) { toast.error("Update failed"); setZ(prev); }
    finally { setBusy(false); }
  };

  const rows = [["landing", "Landing page slot"], ["ticker", "Live scoreboard ticker"], ["queue", "Queue / scoreboard screen"], ["showcase", "Showcase carousel (live + registration)"]];
  return (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="ad-placement-settings">
      <h4 className="font-display font-bold flex items-center gap-2 mb-4"><SlidersHorizontal className="w-4 h-4 text-primary-c" /> Placement Settings</h4>
      <div className="space-y-2">
        {rows.map(([k, label]) => (
          <div key={k} className="flex items-center justify-between glass rounded-xl px-4 py-2.5">
            <span className="text-sm font-medium">{label}</span>
            <button data-testid={`ad-toggle-zone-${k}`} disabled={busy} onClick={() => toggle(k)}
              aria-pressed={z[k]}
              className="relative w-11 h-6 rounded-full transition-colors shrink-0"
              style={{ background: z[k] ? "var(--primary)" : "var(--border)" }}>
              <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform" style={{ transform: z[k] ? "translateX(20px)" : "none" }} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-2)" }}>Showcase carousel size</div>
        <div className="glass rounded-full p-0.5 flex gap-0.5" data-testid="showcase-size-picker">
          {[["small", "Small"], ["medium", "Medium"], ["large", "Large (⅓)"]].map(([v, l]) => (
            <button key={v} data-testid={`showcase-size-${v}`} disabled={busy} onClick={() => setSize(v)}
              className="rounded-full px-3 py-1.5 text-xs font-bold transition-colors"
              style={(z.showcase_size || "large") === v ? { background: "var(--primary)", color: "var(--on-primary)" } : { color: "var(--text-2)" }}>{l}</button>
          ))}
        </div>
      </div>
      <p className="text-xs mt-3" style={{ color: "var(--text-2)" }}>Toggle where sponsor ads appear. The Showcase carousel shows zone="showcase" ads on the TV scoreboard, spectator page and registration portal; size controls its screen share and it stacks on mobile.</p>
    </div>
  );
}

export default function AdminAds() {
  const [ads, setAds] = useState(null);

  useEffect(() => {
    api.get("/admin/ads").then(({ data }) => setAds(data || [])).catch(() => setAds(false));
  }, []);

  if (ads === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (ads === false) return <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load ads.</p>;

  return (
    <div className="space-y-5" data-testid="admin-ads">
      <PlacementSettings />
      <AdForm onCreated={(ad) => setAds((a) => [ad, ...a])} />
      <div>
        <h4 className="font-display font-bold flex items-center gap-2 mb-3"><Megaphone className="w-4 h-4 text-primary-c" /> Campaigns <span className="mono text-xs glass rounded-full px-2 py-0.5">{ads.length}</span></h4>
        {ads.length === 0 ? (
          <p className="text-sm rounded-2xl glow-border p-6 text-center" style={{ background: "var(--surface)", color: "var(--text-2)" }}>No campaigns yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="ads-list">
            {ads.map((ad) => (
              <AdCard key={ad.ad_id} ad={ad}
                onChange={(u) => setAds((a) => a.map((x) => (x.ad_id === u.ad_id ? u : x)))}
                onDelete={(id) => setAds((a) => a.filter((x) => x.ad_id !== id))} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
